import { useMutation, UseMutationResult } from 'react-query';

import { useAuth } from 'src/hooks';

import { bulkUploadUrl } from 'src/utils/urls';

type ApiErrorItem = {
  Code: string;
  FieldName: string;
  Message: string;
};

export type BulkUploadData = {
  IsSuccess: boolean;
  Errors: ApiErrorItem[];
};

type ApiResponse = {
  Data?: string;
  Errors?: ApiErrorItem[];
  ErrorId?: string;
  IsSuccess: boolean;
  StatusCode: number;
};

type TResult = BulkUploadData;

type TVariables = {
  file: File;
};

type TError = Error;

type TSnapshot = unknown;

function setRequestData(params: TVariables): FormData {
  const formData = new FormData();
  formData.append('File', params.file);
  return formData;
}

async function bulkUpload({
  requestData,
  token,
  tokenType,
}: {
  requestData: FormData;
  token: string;
  tokenType: string;
}): Promise<TResult> {
  const response = await fetch(bulkUploadUrl, {
    method: 'POST',
    headers: {
      Authorization: `${tokenType} ${token}`,
    },
    body: requestData,
  });

  let result: ApiResponse | null = null;

  try {
    const text = await response.text();

    result = text ? JSON.parse(text) : null;
  } catch (e) {
    console.error('JSON parse failed:', e);
  }

  if (!result) {
    throw new Error('Invalid server response');
  }

  if (!result.IsSuccess) {
    return {
      IsSuccess: false,
      Errors: result.Errors ?? [],
    };
  }

  return {
    IsSuccess: true,
    Errors: [],
  };
}

function useAddBulkUpload(): UseMutationResult<
  TResult,
  TError,
  TVariables,
  TSnapshot
> {
  const { token, tokenType } = useAuth();

  return useMutation(async (params: TVariables) => {
    const requestData = setRequestData(params);

    return bulkUpload({
      requestData,
      token,
      tokenType,
    });
  });
}

export { useAddBulkUpload };
